import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { legalService } from '../services/legal.service'
import type { TermsOfServiceDocument } from '@/types'

export function TermsOfServicePage() {
  const [doc, setDoc] = useState<TermsOfServiceDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    legalService.getTermsOfService()
      .then(setDoc)
      .catch((reason: unknown) => {
        setError(formatUserFacingError(reason, 'We could not load the terms of service.', 'legal/terms-load'))
      })
      .finally(() => setLoading(false))
  }, [])

  const sections = doc ? [...doc.sections].sort((a, b) => a.order - b.order) : []

  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <h1 className="text-4xl font-bold tracking-tight mb-2">Terms of Service</h1>
      {doc?.lastUpdated && (
        <p className="text-sm text-muted-foreground mb-10">
          Last updated{' '}
          {(typeof (doc.lastUpdated as unknown as { toDate?: () => Date })?.toDate === 'function'
            ? (doc.lastUpdated as unknown as { toDate: () => Date }).toDate()
            : new Date(doc.lastUpdated as unknown as string | number)
          ).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      )}

      {loading ? (
        <div className="space-y-8">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <div className="h-6 w-1/3 rounded bg-muted animate-pulse" />
              <div className="space-y-2">
                <div className="h-4 rounded bg-muted animate-pulse" />
                <div className="h-4 rounded bg-muted animate-pulse w-5/6" />
                <div className="h-4 rounded bg-muted animate-pulse w-4/6" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <p className="text-destructive">{error}</p>
      ) : sections.length === 0 ? (
        <p className="text-muted-foreground">
          {withErrorCode('Terms of service content is not available yet.', 'legal/terms-unavailable')}
        </p>
      ) : (
        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.id}>
              <h2 className="text-xl font-semibold mb-3">{section.title}</h2>
              <div className="prose prose-sm max-w-none text-muted-foreground [&_a]:text-foreground [&_a]:underline [&_strong]:text-foreground">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {section.content}
                </ReactMarkdown>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
